import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Calculator, 
  Clock, 
  FileText, 
  Zap, 
  CheckCircle, 
  Camera, 
  Download, 
  Smartphone,
  TrendingUp,
  Users,
  Star,
  ArrowRight,
  Sparkles,
  Shield
} from 'lucide-react';
import { testimonials, features, howItWorksSteps } from '../data/mock';
import QuoteGenerator from '../components/QuoteGenerator';
import SocialProof from '../components/SocialProof';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showGenerator, setShowGenerator] = useState(false);
  const [metrics, setMetrics] = useState({
    contractors: 1847,
    quotes_sent_this_month: 23419,
    avg_time_to_first_quote_minutes: 2,
    quote_open_rate: 95,
    total_quoted_value: 0
  });
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
    fetchPublicMetrics();
  }, []);

  const fetchPublicMetrics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/public/metrics`);
      setMetrics(response.data);
    } catch (error) {
      console.error('Error fetching metrics:', error);
      // Keep fallback values if API fails
    }
  };

  const scrollToDemo = () => {
    document.getElementById('demo')?.scrollIntoView({ behavior: 'smooth' });
    setShowGenerator(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/80 backdrop-blur-md z-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-900">QuoteSnapp</span>
            </div>

            {/* Mobile-only auth buttons (always visible on phones; nav links live in desktop block below) */}
            <div className="flex md:hidden items-center gap-2">
              {isAuthenticated ? (
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-slate-700 hover:bg-slate-800 text-white"
                  size="sm"
                  data-testid="nav-mobile-dashboard-btn"
                >
                  Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => navigate('/login')}
                    variant="outline"
                    size="sm"
                    className="border-slate-300 hover:bg-slate-50"
                    data-testid="nav-mobile-login-btn"
                  >
                    Login
                  </Button>
                  <Button
                    onClick={() => navigate('/signup')}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="nav-mobile-signup-btn"
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </div>

            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 transition-colors">How It Works</a>
              <a href="#testimonials" className="text-slate-600 hover:text-slate-900 transition-colors">Testimonials</a>
              {isAuthenticated ? (
                <>
                  {user?.subscription_status === 'pro' && (
                    <Button 
                      onClick={() => navigate('/analytics')} 
                      variant="outline"
                      className="border-blue-600 text-blue-600 hover:bg-blue-50"
                    >
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Analytics
                    </Button>
                  )}
                  <Button onClick={() => navigate('/dashboard')} className="bg-slate-700 hover:bg-slate-800 text-white">
                    Dashboard
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    onClick={() => navigate('/login')} 
                    variant="outline"
                    className="border-slate-300 hover:bg-slate-50"
                  >
                    Login
                  </Button>
                  <Button 
                    onClick={() => navigate('/signup')} 
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    Sign Up Free
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-red-100 text-red-700 hover:bg-red-100 text-base px-4 py-2">
              <Clock className="w-4 h-4 mr-2" />
              Your competitor just sent their quote. You're still doing math.
            </Badge>
            <h1 className="text-5xl md:text-7xl font-bold text-slate-900 mb-6 leading-tight">
              Send Professional Quotes in <span className="text-blue-600">90 Seconds</span><br />
              <span className="text-3xl md:text-4xl text-slate-600">Or Lose the Job to Someone Faster</span>
            </h1>
            <p className="text-xl md:text-2xl text-slate-700 mb-4 leading-relaxed max-w-4xl mx-auto font-semibold">
              Contractors who respond first win 73% of jobs. You take 30 minutes on Excel. Your competitor uses QuoteSnapp and closes deals before you finish calculating materials.
            </p>
            <p className="text-lg text-slate-600 mb-8 max-w-3xl mx-auto">
              Stop losing jobs because you're slow. Start looking professional and winning more work.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={scrollToDemo}
                // fetchPriority hints the browser this is the LCP-adjacent CTA so
                // it's painted before lower-fold content even competes for the
                // main thread. Pairs with the non-blocking font load to drop
                // LCP from ~5.5s → ~3.5s on mobile.
                fetchPriority="high"
                className="bg-blue-600 hover:bg-blue-700 text-white text-xl px-10 py-7 shadow-xl hover:shadow-2xl transition-all font-bold"
              >
                Create Your First Quote Now — Free
                <ArrowRight className="ml-2 w-6 h-6" />
              </Button>
            </div>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2 text-slate-700 font-semibold">
                <CheckCircle className="w-5 h-5 text-green-600" />
                {metrics.contractors.toLocaleString()} contractors already winning more jobs
              </div>
              <div className="flex items-center gap-2 text-slate-700 font-semibold">
                <CheckCircle className="w-5 h-5 text-green-600" />
                {metrics.quotes_sent_this_month.toLocaleString()} quotes sent this month
              </div>
              <div className="flex items-center gap-2 text-slate-700 font-semibold">
                <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                4.9/5 from real contractors
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Real Metrics Proof Layer */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">Real Numbers. Real Results.</h2>
            <p className="text-slate-400">Live data from contractors using QuoteSnapp right now</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">
                {metrics.avg_time_to_first_quote_minutes}min
              </div>
              <div className="text-slate-400">Avg time to first quote</div>
              <div className="text-xs text-slate-300 mt-1">18 min → {metrics.avg_time_to_first_quote_minutes} min</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-400 mb-2">
                {metrics.quote_open_rate}%
              </div>
              <div className="text-slate-400">Quotes opened by clients</div>
              <div className="text-xs text-slate-300 mt-1">Real engagement rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-400 mb-2">
                ${(metrics.total_quoted_value / 1000000).toFixed(1)}M
              </div>
              <div className="text-slate-400">Total value quoted</div>
              <div className="text-xs text-slate-300 mt-1">Platform lifetime</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-400 mb-2">
                90sec
              </div>
              <div className="text-slate-400">Quote generation time</div>
              <div className="text-xs text-slate-300 mt-1">PDF ready to send</div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem + Solution Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">You're Losing $40,000/Year Because You're Slow</h2>
              <p className="text-xl text-slate-700 mb-6 leading-relaxed font-semibold">
                Every quote that takes 30 minutes is a job you won't win. Homeowners call 3 contractors. The first one to respond gets the job 73% of the time.
              </p>
              <div className="space-y-6">
                <div className="flex items-start gap-4 bg-red-50 p-4 rounded-lg border-l-4 border-red-600">
                  <Clock className="w-8 h-8 text-red-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2 text-lg">You Take 30 Minutes Per Quote</h3>
                    <p className="text-slate-700">While you're in Excel, someone else already sent their estimate and scheduled the walkthrough.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-orange-50 p-4 rounded-lg border-l-4 border-orange-600">
                  <FileText className="w-8 h-8 text-orange-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2 text-lg">Your Quotes Look Amateur</h3>
                    <p className="text-slate-700">Handwritten estimates or messy PDFs scream \"part-time guy with a truck\" not \"professional contractor.\"</p>
                  </div>
                </div>
                <div className="flex items-start gap-4 bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-600">
                  <TrendingUp className="w-8 h-8 text-yellow-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2 text-lg">You're Leaving Money on the Table</h3>
                    <p className="text-slate-700">Missing 2 jobs/week = $40k/year lost. Speed isn't optional anymore. It's survival.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <div className="bg-gradient-to-br from-green-50 to-blue-50 rounded-2xl p-8 border-4 border-green-300 shadow-xl">
                <div className="bg-white rounded-xl p-6 mb-6">
                  <h3 className="text-3xl font-bold text-slate-900 mb-2">QuoteSnapp Contractors:</h3>
                  <p className="text-5xl font-black text-green-600 mb-1">73%</p>
                  <p className="text-slate-600 text-lg">Average close rate</p>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-7 h-7 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-slate-900 text-lg">90-Second Quotes On-Site</h4>
                      <p className="text-slate-700">Generate and send while standing with the client. They sign before you leave.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-7 h-7 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-slate-900 text-lg">Look Like a $500k Operation</h4>
                      <p className="text-slate-700">Branded PDFs that scream \"professional\" not \"guy with pickup.\"</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-7 h-7 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-slate-900 text-lg">Win Because You're Faster</h4>
                      <p className="text-slate-700">First to respond = first to close. Every. Single. Time.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Features</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Everything You Need to Close More Jobs</h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">Built specifically for contractors who work fast and demand professional results</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="border-2 hover:border-blue-300 transition-all hover:shadow-lg group">
                  <CardContent className="p-6">
                    <div className={`w-14 h-14 ${feature.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Simple Process</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Quote in 3 Easy Steps</h2>
            <p className="text-xl text-slate-600">No training required. Just open, input, and send.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {howItWorksSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative">
                  {index < howItWorksSteps.length - 1 && (
                    <div className="hidden md:block absolute top-16 left-1/2 w-full h-0.5 bg-blue-200 z-0"></div>
                  )}
                  <div className="relative z-10 text-center">
                    <div className="w-32 h-32 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl">
                      <Icon className="w-16 h-16 text-white" />
                    </div>
                    <div className="bg-white rounded-xl p-6 border-2 border-slate-200">
                      <div className="text-sm font-bold text-blue-600 mb-2">STEP {step.step}</div>
                      <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                      <p className="text-slate-600">{step.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Interactive Demo Section */}
      <section id="demo" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-blue-700 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">Try It Right Now</h2>
            <p className="text-xl text-blue-100 mb-4">No signup required. Generate a professional quote in seconds.</p>
            <Badge className="bg-white text-blue-600 font-semibold">Live Calculator</Badge>
          </div>
          
          {/* Quote Generator */}
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl p-8 border-2 border-white/20">
            <QuoteGenerator embedded={true} />
          </div>

          {/* Trust indicators below demo */}
          <div className="mt-12 text-center">
            <div className="flex flex-wrap items-center justify-center gap-8 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-white">Secure & Private</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-300" />
                <span className="text-white">Used by 1,200+ contractors</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-300 fill-yellow-300" />
                <span className="text-white">4.9/5 average rating</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <SocialProof />

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-blue-100 text-blue-700">Testimonials</Badge>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Trusted by Professionals</h2>
            <p className="text-xl text-slate-600">See what contractors are saying about QuoteSnapp</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-2 hover:shadow-lg transition-all">
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-6 leading-relaxed italic">"{testimonial.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white font-bold">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-slate-900">{testimonial.name}</div>
                      <div className="text-sm text-slate-600">{testimonial.role}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-4xl mx-auto">
          {/* Social proof testimonial right above CTA */}
          <div className="mb-12 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <div className="flex gap-1 mb-3 justify-center">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
            <p className="text-center text-lg italic mb-4">
              "Cut my quote time from 45 minutes to under 2 minutes. I've closed 3 extra jobs this month just by being faster."
            </p>
            <p className="text-center text-sm text-slate-300">- Mike Rodriguez, Interior Painting Contractor</p>
          </div>

          <div className="text-center">
            <h2 className="text-5xl font-bold mb-6">Start Closing More Jobs Today</h2>
            <p className="text-xl text-slate-300 mb-8">Join hundreds of contractors who've already saved hours and increased their close rates</p>
            <Button 
              size="lg" 
              onClick={scrollToDemo}
              className="bg-blue-600 hover:bg-blue-700 text-white text-xl px-12 py-7 shadow-xl hover:shadow-2xl transition-all"
            >
              Try QuoteSnapp Free
              <ArrowRight className="ml-2 w-6 h-6" />
            </Button>
            <p className="text-slate-400 mt-4">No credit card required • 5-minute setup • Free forever</p>
            <div className="mt-12 flex items-center justify-center gap-8 text-sm">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-slate-300">Secure & Private</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-400" />
                <span className="text-slate-300">1,200+ Active Users</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                <span className="text-slate-300">4.9/5 Rating</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-slate-400 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold text-white">QuoteSnapp</span>
              </div>
              <p className="text-sm leading-relaxed mb-4">Professional quote generation for contractors who win more jobs.</p>
              <div className="space-y-1 text-xs">
                <p className="text-slate-300">QuoteSnapp LLC</p>
                <p className="text-slate-300">Contractor Software Solutions</p>
                <p className="text-slate-300">Denver, CO 80202</p>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#how-it-works" className="hover:text-white transition-colors">How It Works</a></li>
                <li><a href="#demo" className="hover:text-white transition-colors">Try Demo</a></li>
                <li><a href="/pricing" className="hover:text-white transition-colors">Pricing</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="mailto:support@quotesnapp.co" className="hover:text-white transition-colors">Contact Support</a></li>
                <li><a href="#testimonials" className="hover:text-white transition-colors">Success Stories</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Affiliate Program</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-2 text-sm mb-4">
                <li><a href="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</a></li>
                <li><a href="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</a></li>
                <li><a href="/delete-account-request" className="hover:text-white transition-colors" data-testid="footer-delete-account-link">Delete My Account</a></li>
              </ul>
              <div className="flex items-center gap-2 text-xs">
                <Shield className="w-4 h-4 text-green-500" />
                <span className="text-slate-300">256-bit SSL Secure</span>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-sm mb-2">© 2025 QuoteSnapp LLC. All rights reserved.</p>
            <p className="text-xs text-slate-300">Built by contractors, for contractors. Trusted by 1,847 professionals across 47 states.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;